import { useEffect, useRef, useState } from "react";

const OCTAVE_CENTERS = [500, 1000, 2000, 4000, 8000];
const SQRT2 = Math.SQRT2;

// ANSI S3.1 maximum permissible ambient noise levels
// Insert earphones, test range 500–8000 Hz, testing to 0 dB HL
const DEFAULT_LIMITS_DB_SPL = {
  500: 50,
  1000: 47,
  2000: 49,
  4000: 50,
  8000: 56,
};

const DEFAULT_SPL_OFFSET_DB = 69.2; // REW-calibrated offset
const DEFAULT_FFT_SIZE = 4096;

// iMM-6C frequency response corrections (from CMM30685.cal)
const IMM6C_CAL_CORRECTIONS_DB = {
  500: 0.4,
  1000: 0.0,
  2000: -1.0,
  4000: 0.6,
  8000: 1.9,
};

function octaveEdges(fc) {
  return { f1: fc / SQRT2, f2: fc * SQRT2 };
}

// Returns the average power level (dB) across the frequency band.
// Averaging matches how the REW splOffsetDb calibration was derived
// (single-bin meter reference), so averaging here is correct.
function bandDbFromFreqData(freqDataDb, sampleRate, f1, f2) {
  const nyquist = sampleRate / 2;
  const binCount = freqDataDb.length;
  const hzPerBin = nyquist / binCount;

  const startBin = Math.max(0, Math.floor(f1 / hzPerBin));
  const endBin = Math.min(binCount - 1, Math.ceil(f2 / hzPerBin));

  // Allow single-bin bands (endBin === startBin is valid)
  if (endBin < startBin) return -Infinity;

  let pSum = 0;
  let n = 0;

  for (let i = startBin; i <= endBin; i++) {
    const db = freqDataDb[i];

    // Skip non-finite values (e.g. -Infinity from silent bins)
    if (!Number.isFinite(db)) continue;

    pSum += Math.pow(10, db / 10);
    n++;
  }

  if (n === 0 || pSum <= 0) return -Infinity;

  const pAvg = pSum / n;
  return 10 * Math.log10(pAvg);
}

export function useNoiseMonitor({
  enabled,
  limitsDbSpl = DEFAULT_LIMITS_DB_SPL,
  splOffsetDb = DEFAULT_SPL_OFFSET_DB,
  quietHoldMs = 1000,
  updateHz = 15,
} = {}) {
  const [tooLoud, setTooLoud] = useState(false);
  const [canResume, setCanResume] = useState(true);
  const [estimatedDb, setEstimatedDb] = useState(null);
  const [error, setError] = useState("");

  const audioRef = useRef(null);
  const stateRef = useRef({
    lastUiTs: 0,
    quietMs: 0,
    lastTs: null,
    tooLoud: false,
  });

  const cleanup = async () => {
    const a = audioRef.current;
    audioRef.current = null;

    if (a?.rafId) cancelAnimationFrame(a.rafId);
    if (a?.stream) a.stream.getTracks().forEach((t) => t.stop());

    if (a?.source) {
      try { a.source.disconnect(); } catch {}
    }

    if (a?.analyser) {
      try { a.analyser.disconnect(); } catch {}
    }

    if (a?.ctx) {
      try { await a.ctx.close(); } catch {}
    }

    stateRef.current.lastUiTs = 0;
    stateRef.current.quietMs = 0;
    stateRef.current.lastTs = null;
    stateRef.current.tooLoud = false;

    setTooLoud(false);
    setCanResume(true);
    setEstimatedDb(null);
  };

  useEffect(() => {
    if (!enabled) {
      cleanup();
      return;
    }

    let cancelled = false;

    const start = async () => {
      setError("");

      try {
        await cleanup();

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
          },
        });

        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioCtx();
        const source = ctx.createMediaStreamSource(stream);

        const analyser = ctx.createAnalyser();
        analyser.fftSize = DEFAULT_FFT_SIZE;
        analyser.smoothingTimeConstant = 0.2;
        analyser.minDecibels = -120;
        analyser.maxDecibels = 0;
        source.connect(analyser);

        const freqData = new Float32Array(analyser.frequencyBinCount);
        audioRef.current = { ctx, analyser, source, stream, rafId: null, freqData };

        const minUiDt = 1000 / updateHz;

        const tick = (ts) => {
          if (cancelled || !audioRef.current) return;

          const st = stateRef.current;
          if (!st.lastTs) st.lastTs = ts;

          const dt = ts - st.lastTs;
          st.lastTs = ts;

          analyser.getFloatFrequencyData(freqData);

          let anyTooLoud = false;
          let maxBandDb = -Infinity;

          for (const fc of OCTAVE_CENTERS) {
            const { f1, f2 } = octaveEdges(fc);
            const bandDbfs = bandDbFromFreqData(freqData, ctx.sampleRate, f1, f2);

            if (!Number.isFinite(bandDbfs)) continue;

            const micCorrectionDb = IMM6C_CAL_CORRECTIONS_DB[fc] ?? 0;
            const bandDbSpl = bandDbfs + micCorrectionDb + splOffsetDb;

            if (bandDbSpl > maxBandDb) maxBandDb = bandDbSpl;

            const limit = limitsDbSpl[fc];
            if (limit != null && bandDbSpl > limit) {
              anyTooLoud = true;
            }
          }

          if (anyTooLoud) {
            st.quietMs = 0;
            st.tooLoud = true;
          } else {
            st.quietMs += dt;
            st.tooLoud = false;
          }

          if (!st.lastUiTs || ts - st.lastUiTs >= minUiDt) {
            st.lastUiTs = ts;
            setEstimatedDb(Number.isFinite(maxBandDb) ? maxBandDb : null);
            setTooLoud(anyTooLoud);
            setCanResume(!anyTooLoud && st.quietMs >= quietHoldMs);
          }

          audioRef.current.rafId = requestAnimationFrame(tick);
        };

        audioRef.current.rafId = requestAnimationFrame(tick);
      } catch (e) {
        setError(
          e?.name === "NotAllowedError"
            ? "Microphone permission denied. Please allow microphone access."
            : e?.message || "Failed to start background noise monitor."
        );
        await cleanup();
      }
    };

    start();

    return () => {
      cancelled = true;
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, splOffsetDb, quietHoldMs, updateHz]);

  return { tooLoud, canResume, estimatedDb, error };
}
